// 忠泰門市 打卡 — the store tablet's time clock.
//
// Same backend as the warehouse card on the dashboard (/api/employee/punch*,
// table `time_punches`), so Magnus reads ONE payroll report for warehouse and
// 門市 alike (Performance → 出勤). This page exists because 門市人員 run on the
// limited `counter` role and cannot open the dashboard at all.
//
// Two things the warehouse card does not need:
//  1. The counter login is often the SHARED 'NOKE 門市' account, so a name must
//     be picked before the button unlocks — otherwise a whole month of hours
//     lands on an account that is not a person and payroll is unusable.
//  2. Staff see their own 本月工時 here, including 未打下班卡, so a missed punch
//     gets fixed while someone still remembers the shift.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAuth } from '../contexts/AuthContext';
import timeClockService, {
  PunchStatus,
  PunchType,
  StaffOption,
} from '../services/timeClockService';

// Taiwan has no DST, so a fixed +08:00 offset is safe and keeps the screen
// correct even if the tablet's clock is set to another timezone.
const taipeiHm = (d: Date) =>
  d.toLocaleTimeString('zh-TW', {
    timeZone: 'Asia/Taipei', hour: '2-digit', minute: '2-digit', hour12: false,
  });

const taipeiYmd = (d: Date) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);

// 'MM/DD HH:mm' in Taipei — for a punch that is not from today, where the time
// alone reads as today's (Jen's 10/16 17:12 showed as 「17:12 上班」 on 10/21).
const taipeiMdHm = (d: Date) =>
  `${taipeiYmd(d).slice(5).replace('-', '/')} ${taipeiHm(d)}`;

// 'HH:mm' typed in the 下班 dialog → absolute instant on today's Taipei day.
const taipeiInstantFromHm = (hm: string, base: Date) =>
  new Date(`${taipeiYmd(base)}T${hm}:00+08:00`);


function NokeTimeClockPage() {
  const { currentUser } = useAuth();

  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [staffLoading, setStaffLoading] = useState(true);
  // Distinguishes "the roster really is empty" from "the roster never loaded".
  // Both leave staff = [], but they need OPPOSITE instructions: open accounts
  // vs. the API is unreachable. Showing both at once sends someone off to
  // create staff accounts that already exist.
  const [staffFailed, setStaffFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [status, setStatus] = useState<PunchStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [punching, setPunching] = useState(false);
  const [now, setNow] = useState(new Date());
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  // 下班 dialog state
  const [outOpen, setOutOpen] = useState(false);
  const [outTime, setOutTime] = useState('');
  const [outNote, setOutNote] = useState('');

  // Live Taipei clock.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Punch roster. Preselect only when the logged-in account IS one of the
  // people on it — on the shared tablet login nothing is preselected on
  // purpose, so nobody punches under the wrong name by tapping straight through.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await timeClockService.getCounterStaff();
        if (cancelled) return;
        setStaff(list);
        setStaffFailed(false);
        const self = list.find(s => s.id === currentUser?.id);
        if (self) setSelectedId(prev => (prev === '' ? self.id : prev));
      } catch (err) {
        console.error('Failed to fetch counter staff list:', err);
        if (!cancelled) setStaffFailed(true);
      } finally {
        if (!cancelled) setStaffLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  const refresh = useCallback(async () => {
    if (selectedId === '') { setStatus(null); return; }
    setStatusLoading(true);
    try {
      setStatus(await timeClockService.getStatus(selectedId));
    } catch (err) {
      console.error('Failed to fetch punch status:', err);
    } finally {
      setStatusLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Next punch: 'in' unless the selected person's last punch was an open 'in'
  // that can still be closed (a stale one is left for Magnus to fix).
  const nextType: PunchType = status?.punch_type === 'in' && !status.stale ? 'out' : 'in';
  const selectedName = staff.find(s => s.id === selectedId)?.name || '';
  const ready = selectedId !== '' && !staffLoading;

  const submitPunch = async (type: PunchType, punchedAt?: Date, note?: string) => {
    if (selectedId === '') return false;
    setPunching(true);
    setMessage(null);
    try {
      const row = await timeClockService.punch({
        type, userId: selectedId, punchedAt, note,
      });
      const hm = taipeiHm(new Date(row.punched_at));
      setMessage({
        text: `${selectedName} ${hm} 已打${type === 'in' ? '上班' : '下班'}卡`,
        ok: true,
      });
      await refresh();
      return true;
    } catch (err: any) {
      const apiMessage = err?.response?.data?.message;
      if (err?.response?.status === 409) {
        // Someone already recorded this state — refresh and inform.
        setMessage({ text: apiMessage || '重複打卡', ok: false });
        await refresh();
      } else {
        setMessage({ text: apiMessage || '打卡失敗，請稍後再試', ok: false });
      }
      return false;
    } finally {
      setPunching(false);
    }
  };

  const handleMainButton = () => {
    if (nextType === 'in') {
      submitPunch('in');
      return;
    }
    // 下班 → dialog prefilled with the current Taipei time, so the real leaving
    // time can be entered when there is still tidying up to do after the punch.
    setOutTime(taipeiHm(new Date()));
    setOutNote('');
    setOutOpen(true);
  };

  const handleConfirmOut = async () => {
    if (!/^\d{2}:\d{2}$/.test(outTime)) {
      setMessage({ text: '下班時間格式錯誤', ok: false });
      return;
    }
    const at = taipeiInstantFromHm(outTime, new Date());
    if (Number.isNaN(at.getTime())) {
      setMessage({ text: '下班時間格式錯誤', ok: false });
      return;
    }
    const ok = await submitPunch('out', at, outNote.trim());
    if (ok) setOutOpen(false);
  };

  const timeStr = now.toLocaleTimeString('zh-TW', {
    timeZone: 'Asia/Taipei', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const dateStr = now.toLocaleDateString('zh-TW', {
    timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
  });

  // An open 'in' left over from an EARLIER Taipei day means someone forgot to
  // punch out. Worth flagging here — it is the one attendance problem the person
  // standing at the tablet can still fix — and it leaks no hours, only the fact
  // that the shift was never closed.
  // Past MAX_SHIFT_HOURS (backend `stale`) it can no longer be closed here, so
  // the button goes back to 上班打卡 and the old shift waits for Magnus.
  const staleShift =
    status?.punch_type === 'in' &&
    taipeiYmd(new Date(status.punched_at)) !== taipeiYmd(now);
  const expiredShift = staleShift && !!status?.stale;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccessTimeIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>忠泰門市 打卡</Typography>
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="noke-punch-user">打卡人員</InputLabel>
          <Select
            labelId="noke-punch-user"
            label="打卡人員"
            value={selectedId}
            disabled={staffLoading}
            onChange={(e) => {
              setSelectedId(e.target.value as number);
              setMessage(null);
            }}
          >
            {staff.map(s => (
              <MenuItem key={s.id} value={s.id} sx={{ fontSize: '1.05rem', py: 1.25 }}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Three mutually exclusive states, never two at once. */}
        {!staffLoading && staffFailed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            連不上系統，請重新整理；如果一直失敗請告訴 Magnus。
          </Alert>
        )}

        {!staffLoading && !staffFailed && staff.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            名單是空的，請 Magnus 先開門市人員帳號。
          </Alert>
        )}

        {!staffLoading && !staffFailed && staff.length > 0 && selectedId === '' && (
          <Alert severity="info" sx={{ mb: 2 }}>請先選擇打卡人員。</Alert>
        )}

        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            variant="h2"
            fontWeight={700}
            sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
          >
            {timeStr}
          </Typography>
          <Typography variant="body2" color="text.secondary">{dateStr}</Typography>
          {ready && !statusLoading && status?.punch_type === 'in' && (
            <Chip
              size="small"
              color={staleShift ? 'warning' : 'success'}
              variant="outlined"
              sx={{ mt: 1 }}
              label={staleShift
                ? `${taipeiMdHm(new Date(status.punched_at))} 上班後沒打下班卡`
                : `上班中 — ${taipeiHm(new Date(status.punched_at))} 上班`}
            />
          )}
        </Box>

        {staleShift && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {expiredShift
              ? '上一次忘了打下班卡，已經超過可以補打的時間。請直接打今天的上班卡，並告訴 Magnus 補登那天的下班時間。'
              : '上一次忘了打下班卡。現在按「下班打卡」並填實際離開的時間，超過一天的話請告訴 Magnus 補登。'}
          </Alert>
        )}

        <Button
          fullWidth
          size="large"
          variant="contained"
          color={nextType === 'in' ? 'primary' : 'warning'}
          onClick={handleMainButton}
          disabled={!ready || punching || statusLoading}
          sx={{ py: 2, fontSize: '1.25rem', fontWeight: 700 }}
        >
          {punching || (ready && statusLoading)
            ? <CircularProgress size={26} color="inherit" />
            : (nextType === 'in' ? '上班打卡' : '下班打卡')}
        </Button>

        {/* Hidden while the 下班 dialog is up — the dialog renders its own copy,
            otherwise a rejected punch (時間超出範圍) fails silently behind it. */}
        {message && !outOpen && (
          <Alert severity={message.ok ? 'success' : 'error'} sx={{ mt: 2 }}>
            {message.text}
          </Alert>
        )}
      </Paper>

      <Dialog open={outOpen} onClose={() => setOutOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{selectedName} 下班打卡</DialogTitle>
        <DialogContent>
          {message && !message.ok && (
            <Alert severity="error" sx={{ mt: 1 }}>{message.text}</Alert>
          )}
          <TextField
            label="下班時間"
            type="time"
            value={outTime}
            onChange={(e) => setOutTime(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
            helperText="還要收店再走的話，填實際離開的時間"
          />
          <TextField
            label="備註（選填）"
            value={outNote}
            onChange={(e) => setOutNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            placeholder="例：代墊停車費 60、留下來補貨"
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutOpen(false)} disabled={punching}>取消</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmOut} disabled={punching}>
            {punching ? <CircularProgress size={20} color="inherit" /> : '確認下班'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NokeTimeClockPage;
